import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import strings from "translate/language";
import { addFollowUp, followupList } from "reduxStore/action/followUpActions";
import { getFollowupStatus } from "reduxStore/action/masterActions";
import { handleListResponse, seo } from "utils/utils";
import {
  filterFollowupHistoryRows,
  historyDescription,
  historyDescriptionLabel,
  resolveWalkinRemark,
} from "utils/followupHistoryDisplay";
import Breadcrumb from "component/common/breadcrumb";
import { nextDaycallId } from "../../utils/constants";
import AppointmentScheduleFields from "../Appointment/AppointmentScheduleFields";
import "./FollowupListing.css";
import "../Appointment/AppointmentListing.css";

const formatHistoryWhen = (item) => {
  if (!item?.next_followup_date) return "—";
  const datePart = moment(item.next_followup_date).format("DD-MM-YYYY");
  const timePart =
    item.followup_time && String(item.followup_time).toLowerCase() !== "null"
      ? String(item.followup_time).trim()
      : "";
  return timePart ? `${datePart} · ${timePart}` : datePart;
};

const AppointmentFollowup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formVaild, setFormVaild] = useState({});
  const [viewData, setViewData] = useState([]);
  const [walkinRemark, setWalkinRemark] = useState("");
  const params = useParams();
  const { lead_id = null, appointment_id = null } = params || {};
  const allFollowupDetail = useSelector((state) => state.followUp) || {};
  // Only leave after a submit from this screen — ignore stale addFollowupresponse in Redux.
  const submitPendingRef = useRef(false);

  const navigatpage = async (navname) => {
    navigate(navname);
  };

  const [form, setForm] = useState({
    followup_status: nextDaycallId,
    next_followup_date: "",
    followup_time: "",
    remark: "",
  });

  /**
   * Sidebar: Call / Next Day Call / WhatsApp / Follow-Up only.
   * First empty Call → CM/SCM walk-in; GRE first row keeps its own remark.
   */
  const followupHistory = useMemo(
    () => filterFollowupHistoryRows(viewData),
    [viewData],
  );

  const latestHistory =
    followupHistory.length > 0 ? followupHistory[0] : null;

  useEffect(() => {
    if (allFollowupDetail.response) {
      const { response, addFollowupresponse = null } = allFollowupDetail;
      if (response?.status === 200) {
        const rows = handleListResponse(response.data);
        setViewData(rows);
        setWalkinRemark(resolveWalkinRemark(response, rows));
      } else {
        setViewData([]);
        setWalkinRemark("");
      }
      if (
        submitPendingRef.current &&
        addFollowupresponse &&
        addFollowupresponse?.status === 200
      ) {
        submitPendingRef.current = false;
        navigatpage("/appointment");
      }
    }
  }, [allFollowupDetail]);

  useEffect(() => {
    if (lead_id) {
      dispatch(followupList({ lead_id }));
    }
    dispatch(getFollowupStatus());
  }, [dispatch, lead_id]);

  const statusUpdate = () => {
    if (!handleValid()) {
      submitPendingRef.current = true;
      dispatch(
        addFollowUp({
          followup_status: nextDaycallId,
          next_followup_date: form.next_followup_date,
          followup_time: form.followup_time,
          remark: form.remark,
          lead_id,
          appointment_id,
          // Same as app Follow-up from Appointment Details — creates disposition row after check-in.
          deposition_source: "appointment_details",
        }),
      );
    }
  };

  const handleInputField = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleScheduleDate = (date) => {
    setForm({
      ...form,
      next_followup_date: date ? moment(date).format("YYYY-MM-DD") : "",
    });
  };

  const handleScheduleTime = (followup_time) => {
    setForm({
      ...form,
      followup_time: followup_time || "",
    });
  };

  const handleValid = () => {
    const err = {};
    let formError = false;

    const currentDateTime = moment();
    const selectedDateTime = moment(
      form.next_followup_date + " " + form.followup_time,
      "YYYY-MM-DD hh:mm A",
    );

    if (selectedDateTime.isBefore(currentDateTime, "minute")) {
      if (
        form.next_followup_date === moment().format("YYYY-MM-DD") &&
        form.followup_time
      ) {
        formError = true;
        err.followup_time = strings.invalidTimeError;
      }
    }

    if (!form.next_followup_date) {
      formError = true;
      err.next_followup_date = strings.followup_dateErr;
    }
    if (!form.remark) {
      formError = true;
      err.remark = strings.remarkErr;
    }
    if (!form.followup_time) {
      formError = true;
      err.followup_time = strings.followup_timeErr;
    }

    setFormVaild(err);
    return formError;
  };

  useEffect(() => {
    seo({
      title: "Next Followup",
      metaDescription: "Schedule next followup",
      canonicalLink: window.location.href,
    });
  }, []);

  return (
    <div className="page-body justo-followup-page">
      <Breadcrumb
        title={strings.newFollowup}
        url={"/followup"}
        parentListing={strings.followupList}
        titleactive={strings.NewFollowup}
        isBackBtn={true}
      />
      <div className="container-fluid justo-followup">
        <div className="justo-followup__card justo-followup-form">
          <div className="justo-followup__top justo-followup-form__top">
            <div>
              <h2 className="justo-followup__title">{strings.newFollowup}</h2>
              <p className="justo-followup__subtitle">
                Schedule the next-day call. History stays beside the form.
              </p>
            </div>
            <div className="justo-followup-form__top-actions">
              <button
                type="button"
                className="justo-followup__btn justo-followup__btn--ghost"
                onClick={() => navigatpage("/appointment")}
              >
                {strings.cancel || "Cancel"}
              </button>
            </div>
          </div>

          {latestHistory ? (
            <div className="justo-followup-form__meta">
              <div className="justo-followup-form__fact">
                <span className="justo-followup-form__fact-label">
                  {strings.followupBy}
                </span>
                <span className="justo-followup-form__fact-value">
                  {latestHistory.creator_name || "—"}
                </span>
              </div>
              <div className="justo-followup-form__fact">
                <span className="justo-followup-form__fact-label">
                  {strings.status}
                </span>
                <span className="justo-followup-form__fact-value">
                  {latestHistory.followup_status || "—"}
                </span>
              </div>
              <div className="justo-followup-form__fact">
                <span className="justo-followup-form__fact-label">
                  {strings.date} & {strings.time}
                </span>
                <span className="justo-followup-form__fact-value">
                  {formatHistoryWhen(latestHistory)}
                </span>
              </div>
            </div>
          ) : null}

          <div className="justo-followup-form__body">
            <section className="justo-followup-form__panel">
              <h3 className="justo-followup-form__section-title">
                {strings.NewFollowup || strings.newFollowup}
              </h3>
              <div className="justo-followup-form__grid">
                <div className="justo-followup__field justo-followup-form__field--full">
                  <label className="justo-followup__label">{strings.status}</label>
                  <div className="justo-followup__input justo-followup-form__readonly">
                    {strings.nextDayCall}
                  </div>
                </div>

                <div className="justo-followup__field justo-followup-form__field--full">
                  <AppointmentScheduleFields
                    dateId="next_followup_date"
                    hourId="followup_hour"
                    selectedDate={
                      form.next_followup_date
                        ? moment(form.next_followup_date, "YYYY-MM-DD").toDate()
                        : null
                    }
                    onDateChange={handleScheduleDate}
                    appointmentTime={form.followup_time}
                    onTimeChange={handleScheduleTime}
                    dateError={formVaild.next_followup_date}
                    timeError={formVaild.followup_time}
                    minDate={moment().startOf("day").toDate()}
                    dateLabel={strings.date}
                    timeLabel={strings.time}
                    datePlaceholder={strings.selectAppointmentDate}
                    showTimeHint={false}
                  />
                </div>

                <div className="justo-followup__field justo-followup-form__field--full">
                  <label
                    className="justo-followup__label"
                    htmlFor="followup_remark"
                  >
                    {strings.description}
                    <span className="required-field" />
                  </label>
                  <textarea
                    id="followup_remark"
                    className="justo-followup__input justo-followup-form__textarea"
                    name="remark"
                    value={form.remark}
                    onChange={handleInputField}
                    rows={4}
                    placeholder="Add a short note for this follow-up"
                  />
                  {formVaild.remark ? (
                    <span className="justo-followup__error">
                      {formVaild.remark}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="justo-followup-form__actions">
                <button
                  type="button"
                  className="justo-followup__btn justo-followup__btn--solid"
                  onClick={statusUpdate}
                >
                  <i className="fa fa-check" aria-hidden="true" />
                  {strings.update}
                </button>
              </div>
            </section>

            <section className="justo-followup-form__panel justo-followup-form__panel--history">
              <h3 className="justo-followup-form__section-title">
                Follow-up history
              </h3>
              <div className="justo-followup-form__history-scroll">
                {followupHistory.length > 0 ? (
                  followupHistory.map((item, index) => (
                    <article
                      key={item._id || index}
                      className="justo-followup-form__history-card"
                    >
                      <div className="justo-followup-form__history-head">
                        <span className="justo-followup__status">
                          <i className="fa fa-phone" aria-hidden="true" />
                          <span className="justo-followup__status-label">
                            {item.followup_status || strings.nextDayCall}
                          </span>
                        </span>
                        <span className="justo-followup-form__history-when">
                          {formatHistoryWhen(item)}
                        </span>
                      </div>
                      <dl className="justo-followup-form__history-facts">
                        <div>
                          <dt>{strings.followupBy}</dt>
                          <dd>{item.creator_name || "—"}</dd>
                        </div>
                        <div>
                          <dt>
                            {historyDescriptionLabel(
                              item,
                              walkinRemark,
                              followupHistory,
                            )}
                          </dt>
                          <dd>
                            {historyDescription(
                              item,
                              walkinRemark,
                              followupHistory,
                            )}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  ))
                ) : (
                  <p className="justo-followup-detail__empty">
                    {strings.notavailable}
                  </p>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentFollowup;
